<template>
  <div>
    <evaluator-assignment
      :academic-year-id="academicYearId"
      :bus="bus"
      :visible="showAssigment"
      @cancel="cancelAssignmentHandler"
      @on-add="onAddHandler"
    />

    <div class="score-display-row">
      <section-title
        v-if="data.hasPenalty"
        class="score-display-row__penalty-display"
        container-class="score-display-row__penalty-display__container"
        icon="fas fa-exclamation"
        title="La puntuación final tiene penalidades consideradas."
        title-class="score-display-row__penalty-display__info"
      />
      <custom-percentage
        class="custom-percent"
        :percentage="generalScore"
        title="Puntuación Final"
      />
    </div>

    <section-title
      class="mb-4"
      :has-line="true"
      title="Información de Evaluadores"
    />

    <custom-result-not-found
      v-if="data.evaluations == undefined || data.evaluations.length === 0"
      text="No hay información disponible"
    />

    <div v-for="(value, name) in evaluationsGroup" :key="name">
      <section-title
        v-if="showTitles"
        class="my-4"
        :has-line="false"
        icon="far fa-file-alt"
        :reverse="true"
        :title="name"
      />

      <evalator-row-score
        v-for="(evaluator, index) in value"
        :key="`rows-list-selected-${index}`"
        class="mb-3"
        :evaluator="evaluator"
        :index="index"
        :rubric-id="rubricId"
        @on-remove="onRemoveHandler(evaluator, index)"
      />

      <div v-if="showTitles" class="d-flex justify-content-end my-md-4">
        <custom-percentage
          class="custom-percent custom-percent--group bg-secondary-3 p-md-2"
          :percentage="evaluationsGroupScores[name].toString()"
          title="Puntuación Promedio"
        />
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import CustomPercentage from 'custom-percentage';
import SectionTitle from 'SectionTitle';
import CustomResultNotFound from 'custom-result-not-found';
import EvalatorRowScore from '@/views/provider/proposalHistory/components/EvalatorRowScore.vue';
import groupBy from 'lodash/groupBy';
import EvaluatorAssignment from '@/views/provider/proposalHistory/components/EvaluatorAssignment.vue';
import { getProposalRequestEvalutions } from '@/modules/serviceProposalHistory/api';
import proposalEvaluationStatus from '@/utils/constants/proposalEvaluationStatus';

export default {
  components: {
    CustomPercentage,
    SectionTitle,
    CustomResultNotFound,
    EvalatorRowScore,
    EvaluatorAssignment,
  },
  props: {
    bus: {
      type: Vue,
      default: () => new Vue(),
    },
    proposalRequestId: {
      type: Number,
      default: () => 0,
    },
    academicYearId: {
      type: Number,
      default: () => 0,
    },
    rubricId: {
      type: Number,
      default: () => {},
    },
  },
  data: () => ({
    data: {
      evaluations: [],
    },
    generalScore: '-',
    evaluationsGroup: {},
    evaluationsGroupScores: {},
    showTitles: true,
    showAssigment: false,
  }),
  watch: {
    data() {
      this.mapAndLoadProposalRequestData();
    },
  },
  created() {
    this.bus.$on('showAssigment', () => {
      this.showAssigment = true;
    });
  },
  async mounted() {
    await this.initProposalRequestEvalutions();
    this.$emit('ready');
  },
  methods: {
    async initProposalRequestEvalutions() {
      await getProposalRequestEvalutions(this.proposalRequestId).then((res) => {
        this.data = { ...res.data };
      });
    },
    async mapAndLoadProposalRequestData() {
      const data = { ...this.data };

      const evaluationsGroups = groupBy(
        data.evaluations,
        'proposalEvaluationTypeName'
      );

      const scores = {};
      for (let evaluationGroup in evaluationsGroups) {
        const validsEvaluations = evaluationsGroups[evaluationGroup].filter(
          (x) =>
            x.proposalEvaluationStatusId ===
              proposalEvaluationStatus.Evaluada && !x.isSpecialized
        );
        let score = 0;

        if (validsEvaluations.length == 1) {
          score = validsEvaluations[0].evaluationResult;
        } else if (validsEvaluations.length > 1) {
          score = Number(
            (
              validsEvaluations.reduce((p, c) => {
                return Number(p) + Number(c.evaluationResult);
              }, 0) / validsEvaluations.length
            ).toFixed(1)
          );
        }

        scores[evaluationGroup] = score;
      }

      this.showTitles = Object.keys(evaluationsGroups).length > 1;
      this.evaluationsGroup = evaluationsGroups;
      this.evaluationsGroupScores = scores;

      const scoresValues = Object.values(scores);
      this.generalScore = Number(
        (scoresValues.reduce((p, c) => p + c) / scoresValues.length).toFixed(1)
      );
      this.$emit('score-change', this.generalScore);
    },
    cancelAssignmentHandler() {
      this.showAssigment = false;
    },
    onRemoveHandler(evaluator, index) {
      const tempGroup = [
        ...this.evaluationsGroup[evaluator.proposalEvaluationTypeName],
      ];

      tempGroup.splice(index, 1);
      this.evaluationsGroup[evaluator.proposalEvaluationTypeName] = tempGroup;
      this.initProposalRequestEvalutions();
      this.$emit('refresh');
    },
    onAddHandler() {
      this.initProposalRequestEvalutions();
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/shared/scss/config/fonts.scss';
@import '~@/shared/scss/config/colors.scss';

::v-deep .score-display-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;

  &__penalty-display {
    margin-bottom: 0rem !important;
    margin-right: 0.5rem;

    .title-icon {
      font-size: 0.75rem;
    }

    .dot {
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: map-get($colors, 'yellow-star');
      width: 1.4375rem;
      height: 1.4375rem;
    }

    &__container {
      display: flex !important;
      justify-content: flex-start;
      align-items: center;
      background: map-get($colors, 'gray-9');
      padding: 0.5rem 1rem !important;
    }

    &__info {
      font-size: 0.8571rem;
      font-weight: normal !important;
      margin: 0rem !important;
    }
  }
}

.label {
  font-weight: normal;
  font-size: 0.875rem;
  color: map-get($colors, 'secondary-6');
}

.info {
  font-weight: normal;
  color: map-get($colors, 'black');
}
.custom-percent {
  &--group {
    width: 250px;
  }
}

@media only screen and (max-width: 768px) {
  .score-display-row {
    flex-direction: column;
    &__penalty-display {
      margin: 0rem 0rem 0.5rem !important;
    }
  }
  .actions-dropdown {
    ::v-deep {
      .dropdown-toggle {
        width: 100%;
        font-size: 0.875rem;
      }
    }
  }
}
</style>
